import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, ChartBox, DataTable, KpiGrid, LineChart } from "@netiv/ui-core";
import { WorkflowDetailsModal } from "./WorkflowDetailsModal";

type WindowMonths = 1 | 2 | 3 | 6 | 9 | 12;
type WindowCounts = {
  window_months: WindowMonths;
  open: number;
  unattended: number;
  critical: number;
  closed: number;
  escalation_required: number;
};
type ExceptionRow = {
  exception_id: string;
  workflow_id: string;
  category_id?: string;
  process_stage: string;
  exception_code: string;
  description?: string;
  severity: string;
  status: string;
  unattended: boolean;
  escalation_required: boolean;
  owner_team?: string;
  detected_at: string;
  expected_resolution_at?: string;
  source_system: string;
  source_entity_id?: string;
};
type ExceptionDashboard = {
  selected_months: WindowMonths;
  windows: WindowCounts[];
  exceptions: ExceptionRow[];
};

const WINDOWS: WindowMonths[] = [1, 2, 3, 6, 9, 12];
const API_BASE = import.meta.env.VITE_API_GATEWAY_URL ?? import.meta.env.VITE_QUERY_API_URL ?? "";
const STATE_API_BASE = import.meta.env.VITE_API_GATEWAY_URL ?? import.meta.env.VITE_STATE_API_URL ?? "";

export function OperationsExceptions() {
  const [months, setMonths] = useState<WindowMonths>(1);
  const [data, setData] = useState<ExceptionDashboard | null>(null);
  const [error, setError] = useState("");
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState("");

  const requestAction = (item: ExceptionRow, actionType: "acknowledge" | "escalate" | "create_ticket" | "send_email") => {
    setActionMessage("");
    fetch(`${STATE_API_BASE}/operations/exceptions/${item.exception_id}/actions`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action_type: actionType,
        assigned_team: item.owner_team,
        external_system: actionType === "create_ticket" ? "ticketing" : actionType === "send_email" ? "email" : undefined,
        idempotency_key: `${item.exception_id}:${actionType}:${crypto.randomUUID()}`,
        request_payload: { workflow_id: item.workflow_id, exception_code: item.exception_code },
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Action API returned ${response.status}`);
        return response.json();
      })
      .then((result) => setActionMessage(`Action ${result.status}. NETIV will track the external acknowledgement.`))
      .catch(() => setActionMessage("Action failed"));
  };

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError("");
    fetch(`${API_BASE}/operations/exceptions?months=${months}`, { signal: controller.signal, credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error(`Exceptions API returned ${response.status}`);
        return response.json();
      })
      .then(setData)
      .catch((reason) => {
        if (reason.name !== "AbortError") setError(reason.message);
      });
    return () => controller.abort();
  }, [months]);

  const selected = useMemo(
    () => data?.windows.find((item) => item.window_months === months),
    [data, months],
  );

  return (
    <>
      <div className="netiv-ops-horizons" role="tablist" aria-label="Exception reporting period">
        {WINDOWS.map((value) => (
          <button
            key={value}
            role="tab"
            aria-selected={value === months}
            className={`netiv-role-pill ${value === months ? "netiv-role-pill--on" : ""}`}
            onClick={() => setMonths(value)}
          >
            {value}M
          </button>
        ))}
      </div>

      {error && <Card title="Data unavailable"><span role="alert" /></Card>}
      {!data && !error && <Card title="Exceptions"><span aria-busy="true" /></Card>}

      {data && selected && (
        <>
          {actionMessage && <Card title="Action status"><span style={{ fontSize: 13, color: "var(--netiv-text-2)" }}>{actionMessage}</span></Card>}
          <KpiGrid items={[
            { label: `Open · ${months}M`, value: selected.open, tone: selected.open ? "warn" : "up" },
            { label: "Unattended", value: selected.unattended, tone: selected.unattended ? "down" : "up" },
            { label: "Critical", value: selected.critical, tone: selected.critical ? "down" : "up" },
            { label: "Closed", value: selected.closed, tone: "up" },
            { label: "Escalation required", value: selected.escalation_required, tone: selected.escalation_required ? "down" : "up" },
          ]} />

          <Card title="Cumulative exception counts by reporting window">
            <ChartBox size="md">
              <LineChart data={{
                labels: data.windows.map((item) => `${item.window_months}M`),
                datasets: [
                  { label: "Open", data: data.windows.map((item) => item.open), borderColor: "#d97706", backgroundColor: "rgba(217,119,6,.08)", tension: .3 },
                  { label: "Unattended", data: data.windows.map((item) => item.unattended), borderColor: "#8b1a1a", tension: .3 },
                  { label: "Critical", data: data.windows.map((item) => item.critical), borderColor: "#c0392b", tension: .3 },
                  { label: "Closed", data: data.windows.map((item) => item.closed), borderColor: "#1a7a3f", tension: .3 },
                  { label: "Escalation", data: data.windows.map((item) => item.escalation_required), borderColor: "#1a4fa0", tension: .3 },
                ],
              }} options={{ plugins: { legend: { display: true, position: "bottom" } } }} />
            </ChartBox>
          </Card>

          <Card title="Exception work queue">
            <DataTable
              columns={["Exception", "Stage", "Category", "Severity", "Status", "Owner", "Source", "Detected", "NETIV actions"]}
              rows={data.exceptions.map((item) => [
                <span style={{ fontWeight: 600 }}>{item.exception_code}<br /><small>{item.description ?? item.exception_id}</small></span>,
                item.process_stage,
                item.category_id ?? "—",
                <Badge tone={item.severity === "critical" || item.severity === "high" ? "down" : "warn"}>{item.severity}</Badge>,
                item.status,
                item.owner_team ?? "Unassigned",
                <span>{item.source_system}<br /><small>{item.source_entity_id ?? "—"}</small></span>,
                new Date(item.detected_at).toLocaleString(),
                <span className="netiv-ops-actions" onClick={(event) => event.stopPropagation()}>
                  <button onClick={() => requestAction(item, "acknowledge")}>Acknowledge</button>
                  <button onClick={() => requestAction(item, "escalate")}>Escalate</button>
                  <button onClick={() => requestAction(item, "create_ticket")}>Ticket</button>
                  <button onClick={() => requestAction(item, "send_email")}>Email</button>
                </span>,
              ])}
              onRowClick={(index) => setWorkflowId(data.exceptions[index].workflow_id)}
            />
          </Card>
        </>
      )}

      {workflowId && <WorkflowDetailsModal workflowId={workflowId} onClose={() => setWorkflowId(null)} />}
    </>
  );
}
